import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, ArrowUpRight, ShieldCheck, Radio, Download } from 'lucide-react';
import { EQUIPMENT, SERVICES, INDUSTRIES, ARTICLES, FAQS, MANIFESTO, EXPERIENCE, SITE, IMG } from '../data/site';
import { Seo } from '../lib/seo';
import { Reveal, MaskedLines, Marquee, SpotlightImage, Chapter } from '../components/motion';
import { CTABand, LinkRow } from '../components/chrome';
import { FAQ } from '../components/FAQ';
import { RFQForm } from '../components/RFQForm';

const MARQUEE_ITEMS = ['GC/MS SYSTEMS', 'FTIR SPECTROMETERS', 'RAMAN SPECTROSCOPY', 'XRF ANALYZERS', 'HANDHELD MASS SPECTROMETRY', 'GAS & VAPOR DETECTION', 'AIR MONITORING', 'CHEMISTRY ANALYZERS', 'IQ/OQ/PQ VALIDATION', 'COMMISSIONING', 'PREVENTIVE MAINTENANCE', 'NATIONWIDE SUPPORT'];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  return (
    <div data-testid="page-home">
      <Seo
        title="Analytical Equipment Supplier & Government Laboratory Contractor | ToxGlobal Analytics"
        description="ToxGlobal Analytics supplies, installs, and supports analytical instrumentation — GC/MS, FTIR, Raman, XRF and more — for government and regulated laboratories nationwide. SAM.gov Active. Request a quote or send an RFQ today."
        path="/"
      />

      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative overflow-hidden bg-paper" data-testid="home-hero">
        <div className="container-x grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center pt-14 pb-16 sm:pt-20 sm:pb-24 lg:pt-24 lg:pb-28">
          <div>
            <Reveal>
              <div className="flex flex-wrap items-center gap-2 mb-7">
                <span className="inline-flex items-center gap-2 bg-ink text-white font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] px-3 py-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" data-testid="hero-sam-badge" /> SAM.gov Active
                </span>
                <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-slate-500 border border-slate-300 px-3 py-1.5">
                  UEI {SITE.uei} · CAGE {SITE.cage}
                </span>
              </div>
            </Reveal>
            <h1 className="display text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[4.6rem] text-ink" data-testid="hero-headline">
              <MaskedLines
                lines={[
                  <span key="a">ANALYTICAL</span>,
                  <span key="b">EQUIPMENT. <em className="text-signal not-italic">INSTALLED.</em></span>,
                  <span key="c">SUPPORTED.</span>,
                ]}
                delay={0.15}
              />
            </h1>
            <Reveal delay={0.55}>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="hero-subline">
                ToxGlobal Analytics sources, installs, and supports analytical instrumentation for government and regulated
                laboratories — from GC/MS and FTIR to XRF and air monitoring — nationwide, from a single accountable contractor.
              </p>
            </Reveal>
            <Reveal delay={0.65}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-request-quote">Request a Quote <ArrowRight size={15} /></Link>
                <Link to="/government" className="btn-ghost" data-testid="hero-cta-send-rfq">Send an RFQ</Link>
              </div>
            </Reveal>
            <Reveal delay={0.75}>
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                {SITE.payment.map((p) => <span key={p} className="flex items-center gap-2"><ShieldCheck size={13} className="text-signal" /> {p}</span>)}
              </div>
            </Reveal>
          </div>

          <motion.div style={{ y: imgY }} className="relative" data-testid="hero-visual">
            <SpotlightImage src={IMG.hero} alt="Analytical laboratory with scientist operating mass spectrometry instrumentation" ratio="aspect-[4/5] sm:aspect-[16/14] lg:aspect-[5/6]" parallax={false} />
            <div className="absolute -bottom-5 left-4 sm:left-6 bg-ink text-white px-5 py-4 shadow-2xl" data-testid="hero-float-card">
              <p className="eyebrow text-signal">Government-ready</p>
              <p className="mt-1 text-sm font-medium">Prime or subcontractor engagement</p>
            </div>
          </motion.div>
        </div>

        <div className="hairline-t bg-white">
          <Marquee items={MARQUEE_ITEMS} className="py-4" />
        </div>
      </section>

      {/* ---------- STATS ---------- */}
      <section className="bg-white hairline-b" data-testid="home-stats">
        <div className="container-x grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-200">
          {[
            ['18', 'Equipment categories sourced'],
            ['15', 'Lifecycle support services'],
            ['10', 'Industry sectors served'],
            ['50', 'States — nationwide coverage'],
          ].map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.06} className="py-10 px-4 sm:px-8">
              <p className="display text-4xl sm:text-5xl text-ink tabular">{n}</p>
              <p className="mt-2 text-sm text-slate-500">{l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- MANIFESTO ---------- */}
      <section className="section-pad" data-testid="home-manifesto">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-signal mb-3">The operating model</p>
            <h2 className="display text-3xl sm:text-5xl text-ink max-w-3xl">One accountable partner — from sourcing through lifecycle.</h2>
            <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed">
              Supporting government and regulated laboratories from equipment sourcing through installation, validation, service, and lifecycle support.
            </p>
          </Reveal>
          <div className="mt-10 border-t border-slate-200">
            {MANIFESTO.map((c) => <Chapter key={c.n} {...c} />)}
          </div>
        </div>
      </section>

      {/* ---------- EQUIPMENT PREVIEW ---------- */}
      <section className="bg-white section-pad hairline-t" data-testid="home-equipment">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <Reveal>
              <p className="eyebrow text-signal mb-3">01 / Equipment</p>
              <h2 className="display text-3xl sm:text-5xl text-ink">Instrumentation we source & support</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/equipment" className="btn-ghost" data-testid="home-equipment-view-all">All 18 categories <ArrowUpRight size={15} /></Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {EQUIPMENT.slice(0, 6).map((e, i) => (
              <Reveal key={e.slug} delay={Math.min(i * 0.05, 0.25)} className="bg-white">
                <Link to={`/equipment/${e.slug}`} className="group block p-7 h-full hover:bg-slate-50 transition-colors" data-testid={`home-equipment-card-${e.slug}`}>
                  <p className="font-mono text-[11px] text-slate-400 tabular">{String(i + 1).padStart(2, '0')}</p>
                  <h3 className="display text-xl text-ink mt-3 group-hover:text-signal transition-colors">{e.name}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{e.tagline}</p>
                  <ArrowRight size={16} className="mt-5 text-slate-400 group-hover:text-signal group-hover:translate-x-1 transition-all" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SERVICES INDEX ---------- */}
      <section className="section-pad" data-testid="home-services">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <Reveal>
              <p className="eyebrow text-signal mb-3">02 / Services</p>
              <h2 className="display text-3xl sm:text-5xl text-ink">Procurement through lifecycle support</h2>
            </Reveal>
            <Reveal delay={0.1}><Link to="/services" className="btn-ghost" data-testid="home-services-view-all">All services <ArrowUpRight size={15} /></Link></Reveal>
          </div>
          <div className="border-t border-slate-200">
            {SERVICES.map((s, i) => <LinkRow key={s.slug} i={i} href={`/services/${s.slug}`} label={s.name} tag={s.tagline} />)}
          </div>
        </div>
      </section>

      {/* ---------- GOVERNMENT ---------- */}
      <section className="spotlight-dark grain text-white section-pad" data-testid="home-government">
        <div className="container-x grid lg:grid-cols-[1fr_0.9fr] gap-12 items-center">
          <div>
            <Reveal>
              <p className="eyebrow text-signal mb-3">03 / Government contracting</p>
              <h2 className="display text-3xl sm:text-5xl">Built for federal procurement</h2>
              <p className="mt-5 text-slate-300 leading-relaxed max-w-xl">
                RFQ response capability, purchase card and PO fulfillment, OEM/distributor sourcing, installation and
                commissioning, documentation and acceptance support, and nationwide service capability — as prime contractor or subcontractor.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 grid grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-lg font-mono text-sm">
                <div className="bg-ink/60 p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">UEI</p><p className="mt-1 text-white">{SITE.uei}</p></div>
                <div className="bg-ink/60 p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">CAGE</p><p className="mt-1 text-white">{SITE.cage}</p></div>
                <div className="bg-ink/60 p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">SAM.gov</p><p className="mt-1 text-emerald-400">Active</p></div>
                <div className="bg-ink/60 p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">NAICS (primary)</p><p className="mt-1 text-white">334516</p></div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/government" className="btn-light" data-testid="home-gov-cta">Contracting overview <ArrowRight size={15} /></Link>
                <a href="/capability-statement.pdf" download className="btn-outline-light" data-testid="home-capability-download"><Download size={15} /> Capability Statement</a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <SpotlightImage src={IMG.govLab} alt="Government laboratory environment with analytical instrumentation" ratio="aspect-[4/3]" />
            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="eyebrow text-slate-400 mb-4">Substantiated experience</p>
              <ul className="space-y-3">
                {EXPERIENCE.map((x) => (
                  <li key={x.org} className="flex gap-3 text-sm">
                    <Radio size={14} className="text-signal mt-0.5 shrink-0" />
                    <span><span className="text-white font-medium">{x.org}</span> <span className="text-slate-400">— {x.detail}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- INDUSTRIES ---------- */}
      <section className="section-pad" data-testid="home-industries">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <Reveal>
              <p className="eyebrow text-signal mb-3">04 / Industries</p>
              <h2 className="display text-3xl sm:text-5xl text-ink">Who we equip</h2>
            </Reveal>
            <Reveal delay={0.1}><Link to="/industries" className="btn-ghost" data-testid="home-industries-view-all">All industries <ArrowUpRight size={15} /></Link></Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.slug} delay={Math.min(i * 0.04, 0.3)} className="bg-paper">
                <Link to={`/industries/${ind.slug}`} className="group block p-6 h-full hover:bg-white transition-colors" data-testid={`home-industry-card-${ind.slug}`}>
                  <p className="font-mono text-[10px] text-slate-400 tabular">{String(i + 1).padStart(2, '0')}</p>
                  <p className="mt-3 font-display font-semibold text-ink group-hover:text-signal transition-colors leading-snug">{ind.name}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- INSIGHTS ---------- */}
      <section className="bg-white section-pad hairline-t" data-testid="home-insights">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <Reveal>
              <p className="eyebrow text-signal mb-3">05 / Insights</p>
              <h2 className="display text-3xl sm:text-5xl text-ink">Field notes for purchasers</h2>
            </Reveal>
            <Reveal delay={0.1}><Link to="/insights" className="btn-ghost" data-testid="home-insights-view-all">All insights <ArrowUpRight size={15} /></Link></Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ARTICLES.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06} className="h-full">
                <Link to={`/insights/${a.slug}`} className="group flex flex-col h-full border border-slate-200 hover:border-signal transition-colors" data-testid={`home-article-card-${a.slug}`}>
                  <div className="overflow-hidden aspect-[16/10] bg-midnight">
                    <img src={a.image} alt={a.alt} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 flex flex-col grow">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">{a.read}</p>
                    <h3 className="mt-2 font-display font-semibold text-ink leading-snug group-hover:text-signal transition-colors">{a.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed grow">{a.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FAQ faqs={FAQS} />

      {/* ---------- RFQ ---------- */}
      <section className="section-pad bg-slate-50 hairline-t" data-testid="home-rfq">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-signal mb-3">Request a quote</p>
            <h2 className="display text-3xl sm:text-5xl text-ink mb-8 max-w-3xl">Have a requirement? Submit it in under a minute.</h2>
          </Reveal>
          <RFQForm />
        </div>
      </section>

      <CTABand />
    </div>
  );
}
